import { Box, Flex, Skeleton, Typography } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';

import type { LineItem, LineItemsGridProps } from './lineItemsGrid.types';

const insertIf = (condition: boolean, value: LineItem) =>
  condition ? [value] : [];

export const LineItemsGrid = ({
  color,
  deliveryFee,
  deliveryLabel = 'delivery',
  discountTotal,
  feeTax,
  hasDeliveryAddress = false,
  hasJanePayServiceFee = false,
  isRecalculating = false,
  janePayEnabled = false,
  janePayServiceFeeAmount,
  preDiscountSubtotal,
  reservationMode,
  reservationModeLabel,
  roundingAmount,
  salesTax,
  selectedPaymentMethod,
  serviceFee,
  showTip = false,
  storeTax,
  taxIncluded,
  tipAmount,
}: LineItemsGridProps) => {
  const applyJanePayServiceFee = Boolean(
    selectedPaymentMethod === 'jane_pay' &&
    janePayServiceFeeAmount &&
    janePayServiceFeeAmount > 0
  );
  const estimatedServiceFeeTotal =
    (serviceFee || 0) + (applyJanePayServiceFee ? janePayServiceFeeAmount! : 0);

  const serviceFeeDisplay: number | string =
    serviceFee || applyJanePayServiceFee
      ? estimatedServiceFeeTotal
      : janePayEnabled && hasJanePayServiceFee
        ? 'Calculated at checkout'
        : estimatedServiceFeeTotal;

  const lineItems = [
    {
      title: 'Subtotal',
      total: preDiscountSubtotal,
      id: 'subtotal',
    },
    ...insertIf(!!discountTotal && discountTotal > 0, {
      title: 'Estimated savings',
      total: -Number(discountTotal),
      id: 'discountTotal',
    }),
    {
      title: 'Estimated service fee',
      total: serviceFeeDisplay,
      id: 'serviceFee',
    },
    ...insertIf(reservationMode === 'delivery', {
      title: `Estimated ${deliveryLabel} fee`,
      total: hasDeliveryAddress ? deliveryFee : 'Enter address',
      id: 'deliveryFee',
    }),
    ...insertIf(!!feeTax && feeTax > 0, {
      title: 'Estimated fee tax',
      total: feeTax,
      id: 'feeTax',
    }),
    ...insertIf(!!storeTax && storeTax > 0, {
      title: 'Estimated local tax',
      total: storeTax,
      id: 'storeTax',
    }),
    ...insertIf(!taxIncluded, {
      title: 'Estimated sales tax',
      total: salesTax,
      id: 'salesTax',
    }),
    ...insertIf(showTip, {
      title: `${reservationModeLabel} tip`,
      total: tipAmount,
      id: 'tipAmount',
      showZero: true,
    }),
    ...insertIf(roundingAmount != 0, {
      title: 'Rounding',
      total: roundingAmount,
      id: 'roundingAmount',
    }),
  ];

  const recalculatedFields = ['deliveryFee', 'serviceFee'];

  return (
    <Box>
      {lineItems.map((lineItem) => (
        <Flex mt={8} justifyContent="space-between" key={lineItem.id}>
          <Typography color={color}>{lineItem.title}</Typography>
          {isRecalculating && recalculatedFields.includes(lineItem.id) ? (
            <Skeleton.Bone ml="auto" width={50} />
          ) : (
            <Typography color={color} textAlign="right">
              {typeof lineItem.total === 'string'
                ? lineItem.total
                : lineItem.total || lineItem.showZero
                  ? formatCurrency(lineItem.total ?? 0)
                  : 'Free'}
            </Typography>
          )}
        </Flex>
      ))}
    </Box>
  );
};
